/**
 * Newsletter Popup Component
 * Handles popup triggers, form submission, and discount code reveal
 * ============================================
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'newsletter_popup_shown';
  const SUBSCRIBED_KEY = 'newsletter_subscribed';

  class NewsletterPopup {
    constructor(element) {
      this.popup = element;
      this.overlay = element.querySelector('[data-newsletter-overlay]');
      this.form = element.querySelector('[data-newsletter-form]');
      this.formState = element.querySelector('[data-newsletter-form-state]');
      this.successState = element.querySelector('[data-newsletter-success-state]');
      this.errorContainer = element.querySelector('[data-newsletter-error]');
      this.closeButtons = element.querySelectorAll('[data-newsletter-close]');

      // Get settings from data attributes
      this.enabled = this.popup.dataset.enabled === 'true';
      this.triggerType = this.popup.dataset.triggerType || 'delay';
      this.triggerDelay = parseInt(this.popup.dataset.triggerDelay, 10) || 5;
      this.triggerScroll = parseInt(this.popup.dataset.triggerScroll, 10) || 50;
      this.displayFrequency = this.popup.dataset.displayFrequency || 'once_session';
      this.closeOnOverlay = this.popup.dataset.closeOnOverlay === 'true';
      this.enableDiscount = this.popup.dataset.enableDiscount === 'true';
      this.discountCode = this.popup.dataset.discountCode || '';

      this.isOpen = false;
      this.hasTriggered = false;
      this.scrollHandler = null;
      this.exitIntentHandler = null;

      this.init();
    }

    init() {
      if (!this.enabled) return;
      
      // Check if we're returning from a successful form submission
      if (this.checkForSuccessfulSubmission()) {
        return; // Don't show popup again, show success state instead
      }
      
      if (this.shouldSkipDisplay()) return;

      this.bindEvents();
      this.setupTrigger();
    }

    checkForSuccessfulSubmission() {
      // Check if URL contains customer_posted=true (Shopify's success indicator)
      const urlParams = new URLSearchParams(window.location.search);
      const customerPosted = urlParams.get('customer_posted');
      let pendingEmail = null;
      try {
        pendingEmail = localStorage.getItem('newsletter_pending_email');
      } catch (e) { /* Storage unavailable */ }
      
      if (customerPosted === 'true' && pendingEmail) {
        // Clear the pending email
        try { localStorage.removeItem('newsletter_pending_email'); } catch (e) {}
        
        // Mark as subscribed
        try { localStorage.setItem(SUBSCRIBED_KEY, 'true'); } catch (e) {}
        
        // Bind close events first so user can close the popup
        this.bindCloseEvents();
        
        // Show the popup with success state
        this.hasTriggered = true;
        this.isOpen = true;
        this.popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('newsletter-popup-open');
        this.popup.classList.add('is-success');
        
        // Initialize Lucide icons for success state
        if (typeof lucide !== 'undefined') {
          setTimeout(() => lucide.createIcons(), 50);
        }
        
        // Clean up URL (remove query params)
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        return true;
      }
      
      // If there was a pending email but no success, clear it (submission failed or was cancelled)
      if (pendingEmail && !customerPosted) {
        try { localStorage.removeItem('newsletter_pending_email'); } catch (e) {}
      }
      
      return false;
    }

    shouldSkipDisplay() {
      // Skip if user has already subscribed
      try {
        if (localStorage.getItem(SUBSCRIBED_KEY) === 'true') {
          return true;
        }
      } catch (e) { /* Storage unavailable */ }

      const lastShown = this.getLastShownTime();
      if (!lastShown) return false;

      const now = Date.now();
      const elapsed = now - lastShown;

      switch (this.displayFrequency) {
        case 'once_session':
          // Check session storage instead
          try {
            return sessionStorage.getItem(STORAGE_KEY) === 'true';
          } catch (e) {
            return false;
          }
        case 'once_day':
          return elapsed < 24 * 60 * 60 * 1000; // 24 hours
        case 'once_week':
          return elapsed < 7 * 24 * 60 * 60 * 1000; // 7 days
        case 'always':
        default:
          return false;
      }
    }

    getLastShownTime() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? parseInt(stored, 10) : null;
      } catch (e) {
        return null;
      }
    }

    setLastShownTime() {
      try {
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (e) {
        // Storage unavailable, continue without persisting
      }
    }

    bindCloseEvents() {
      // Close button clicks
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Overlay click
      if (this.closeOnOverlay && this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    bindEvents() {
      // Bind close events
      this.bindCloseEvents();

      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      }
    }

    setupTrigger() {
      switch (this.triggerType) {
        case 'delay':
          this.setupDelayTrigger();
          break;
        case 'scroll':
          this.setupScrollTrigger();
          break;
        case 'exit':
          this.setupExitIntentTrigger();
          break;
      }
    }

    setupDelayTrigger() {
      setTimeout(() => {
        if (!this.hasTriggered) {
          this.open();
        }
      }, this.triggerDelay * 1000);
    }

    setupScrollTrigger() {
      this.scrollHandler = () => {
        if (this.hasTriggered) return;

        const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        
        if (scrollPercent >= this.triggerScroll) {
          this.open();
          window.removeEventListener('scroll', this.scrollHandler);
        }
      };

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    setupExitIntentTrigger() {
      this.exitIntentHandler = (e) => {
        if (this.hasTriggered) return;

        // Detect mouse leaving viewport from the top
        if (e.clientY <= 0) {
          this.open();
          document.removeEventListener('mouseout', this.exitIntentHandler);
        }
      };

      document.addEventListener('mouseout', this.exitIntentHandler);
    }

    open() {
      if (this.isOpen || this.hasTriggered) return;

      this.hasTriggered = true;
      this.isOpen = true;
      this.popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('newsletter-popup-open');

      // Set shown time
      this.setLastShownTime();

      // Focus trap - focus first focusable element
      setTimeout(() => {
        const focusable = this.popup.querySelector('input, button:not([data-newsletter-close])');
        if (focusable) focusable.focus();
      }, 100);

      // Initialize Lucide icons if available
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('newsletter-popup-open');
    }

    async handleSubmit(e) {
      const emailInput = this.form.querySelector('input[type="email"]');
      const email = emailInput?.value?.trim();

      if (!email || !this.validateEmail(email)) {
        e.preventDefault();
        this.showError('Please enter a valid email address');
        return;
      }

      // Clear any previous errors
      this.clearError();
      
      // Show loading state
      this.popup.classList.add('is-loading');
      
      // Store email in localStorage before form submits (for success detection on redirect back)
      try { localStorage.setItem('newsletter_pending_email', email); } catch (e) {}
      
      // Let the form submit naturally - Shopify will handle CAPTCHA and redirect
      // The success detection happens on page load (see checkForSuccessfulSubmission)
    }

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    }

    handleSuccess() {
      // Mark as subscribed
      try { localStorage.setItem(SUBSCRIBED_KEY, 'true'); } catch (e) {}

      // Show success state
      this.popup.classList.add('is-success');

      // Initialize Lucide icons for success state
      if (typeof lucide !== 'undefined') {
        setTimeout(() => lucide.createIcons(), 50);
      }
    }

    showError(message) {
      if (this.errorContainer) {
        this.errorContainer.textContent = message;
      }
    }

    clearError() {
      if (this.errorContainer) {
        this.errorContainer.textContent = '';
      }
    }

    destroy() {
      // Clean up event listeners
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.exitIntentHandler) {
        document.removeEventListener('mouseout', this.exitIntentHandler);
      }
    }
  }

  // Initialize on DOM ready
  function init() {
    const popup = document.querySelector('[data-newsletter-popup]');
    if (popup) {
      window.newsletterPopup = new NewsletterPopup(popup);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external control
  window.NewsletterPopup = NewsletterPopup;

  // Allow manual triggering via custom event
  document.addEventListener('newsletter:open', () => {
    if (window.newsletterPopup) {
      window.newsletterPopup.hasTriggered = false;
      window.newsletterPopup.open();
    }
  });

  document.addEventListener('newsletter:close', () => {
    if (window.newsletterPopup) {
      window.newsletterPopup.close();
    }
  });
})();

